import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';

import {
  CommentRepliesPage,
  PostComment,
  PostCommentCreate,
  RestaurantPost,
  RestaurantPostCreate,
  RestaurantPostPage,
  RestaurantPostUpdate,
  RestaurantWallProfile,
  PostPhotoUpload,
  RestaurantWallProfileUpdate,
  SocialEnvelope,
} from '../models/social';
import { ServiceConstant } from '../utils/service-constant.util';

/**
 * Restaurant → Social Media (FNF-1609). Calls social-networking-service
 * through Kong at `/v2/business/social/restaurants/{restaurantId}/...`
 * (FNF-1608). The service checks the token's role (admin, manager, super
 * admin) and that its restaurant is the one in the path.
 *
 * Like the other services here, errors come back as the error value instead
 * of throwing, so callers check for `data`.
 */
@Injectable({
  providedIn: 'root',
})
export class SocialMediaService {
  private httpClient = inject(HttpClient);

  private base(restaurantId: string): string {
    return `${ServiceConstant.apiUrl}/business/social/restaurants/${encodeURIComponent(restaurantId)}`;
  }

  /** 404 until the restaurant is published on Fynkle: profiles are created then. */
  getProfile(restaurantId: string): Observable<SocialEnvelope<RestaurantWallProfile> | any> {
    return this.httpClient
      .get<SocialEnvelope<RestaurantWallProfile>>(`${this.base(restaurantId)}/profile`)
      .pipe(catchError((error) => of(error)));
  }

  /**
   * Edit the handle, tagline or cover (FNF-1683). Only the fields sent
   * change; `null` (or a blank tagline) clears it. 409 = handle taken,
   * 400 = handle invalid or reserved, 404 = not published yet.
   */
  updateProfile(
    restaurantId: string,
    changes: RestaurantWallProfileUpdate,
  ): Observable<SocialEnvelope<RestaurantWallProfile> | any> {
    return this.httpClient
      .put<SocialEnvelope<RestaurantWallProfile>>(`${this.base(restaurantId)}/profile`, changes)
      .pipe(catchError((error) => of(error)));
  }

  /** A presigned POST for one photo (FNF-1688). */
  presignPhoto(
    restaurantId: string,
    contentType: string,
  ): Observable<SocialEnvelope<PostPhotoUpload> | any> {
    return this.httpClient
      .post<SocialEnvelope<PostPhotoUpload>>(`${this.base(restaurantId)}/posts/photo-uploads`, {
        contentType,
      })
      .pipe(catchError((error) => of(error)));
  }

  /**
   * Send the file straight to S3 with the presigned POST: every policy field,
   * then the file last, as S3 requires. TokenInterceptor leaves this request
   * alone (no auth headers). Success is S3's empty 204, so it emits `true`;
   * a refusal (too large, expired) emits the error.
   */
  uploadPhoto(upload: PostPhotoUpload, file: File): Observable<true | any> {
    const form = new FormData();
    Object.entries(upload.fields).forEach(([name, value]) => form.append(name, value));
    form.append('file', file);
    return this.httpClient.post(upload.url, form).pipe(
      map(() => true as const),
      catchError((error) => of(error)),
    );
  }

  getPosts(
    restaurantId: string,
    cursor: string | null = null,
    limit = 20,
  ): Observable<SocialEnvelope<RestaurantPostPage> | any> {
    let params = new HttpParams().set('limit', limit);
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.httpClient
      .get<SocialEnvelope<RestaurantPostPage>>(`${this.base(restaurantId)}/posts`, { params })
      .pipe(catchError((error) => of(error)));
  }

  createPost(
    restaurantId: string,
    post: RestaurantPostCreate,
  ): Observable<SocialEnvelope<RestaurantPost> | any> {
    return this.httpClient
      .post<SocialEnvelope<RestaurantPost>>(`${this.base(restaurantId)}/posts`, post)
      .pipe(catchError((error) => of(error)));
  }

  updatePost(
    restaurantId: string,
    postId: string,
    changes: RestaurantPostUpdate,
  ): Observable<SocialEnvelope<RestaurantPost> | any> {
    return this.httpClient
      .patch<SocialEnvelope<RestaurantPost>>(
        `${this.base(restaurantId)}/posts/${encodeURIComponent(postId)}`,
        changes,
      )
      .pipe(catchError((error) => of(error)));
  }

  /** 204 on success, so success is `null`; an error comes back as the error. */
  deletePost(restaurantId: string, postId: string): Observable<any> {
    return this.httpClient
      .delete(`${this.base(restaurantId)}/posts/${encodeURIComponent(postId)}`)
      .pipe(catchError((error) => of(error)));
  }

  // --- Comments (FNF-1697): read-only, what diners say under a post ---------

  getComments(
    restaurantId: string,
    postId: string,
    limit = 50,
  ): Observable<SocialEnvelope<PostComment[]> | any> {
    const params = new HttpParams().set('limit', limit);
    return this.httpClient
      .get<SocialEnvelope<PostComment[]>>(this.commentsUrl(restaurantId, postId), { params })
      .pipe(catchError((error) => of(error)));
  }

  getCommentReplies(
    restaurantId: string,
    postId: string,
    commentId: string,
    cursor: string | null = null,
    limit = 20,
  ): Observable<SocialEnvelope<CommentRepliesPage> | any> {
    let params = new HttpParams().set('limit', limit);
    if (cursor) {
      params = params.set('cursor', cursor);
    }
    return this.httpClient
      .get<SocialEnvelope<CommentRepliesPage>>(
        `${this.commentsUrl(restaurantId, postId)}/${encodeURIComponent(commentId)}/replies`,
        { params },
      )
      .pipe(catchError((error) => of(error)));
  }

  /** Comments as the restaurant, or replies with parentCommentId (FNF-1698). */
  createComment(
    restaurantId: string,
    postId: string,
    comment: PostCommentCreate,
  ): Observable<SocialEnvelope<PostComment> | any> {
    return this.httpClient
      .post<SocialEnvelope<PostComment>>(this.commentsUrl(restaurantId, postId), comment)
      .pipe(catchError((error) => of(error)));
  }

  /** Deletes one of the restaurant's own comments; diners' are 403. */
  deleteComment(restaurantId: string, postId: string, commentId: string): Observable<any> {
    return this.httpClient
      .delete(`${this.commentsUrl(restaurantId, postId)}/${encodeURIComponent(commentId)}`)
      .pipe(catchError((error) => of(error)));
  }

  private commentsUrl(restaurantId: string, postId: string): string {
    return `${this.base(restaurantId)}/posts/${encodeURIComponent(postId)}/comments`;
  }
}
